import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { WellHeaderInfoService } from 'app/modules/well-header-information/services/well-header-info.service';
import { saveAs } from 'file-saver';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { EMPTY, Subject, map, merge, switchMap, takeUntil } from 'rxjs';
import module from "app/modules/well-header-information/well-header-information.module";
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

@Component({
  selector: 'app-overall-view-modal',
  templateUrl: './overall-view-modal.component.html',
  standalone: true,
  imports: module,
  styleUrls: ['./overall-view-modal.component.scss']
})
export class OverallViewModalComponent {
  @ViewChild(MatPaginator) private _paginator: MatPaginator;
  @ViewChild(MatSort) private _sort: MatSort;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  public fieldList: any = [] // for filter
  public platformList: any = [] // for filter

  dt: any
  searchForm: UntypedFormGroup;
  startingIndex: number = 0
  isLoading: boolean = false

  constructor(
    private wellHeaderService: WellHeaderInfoService,
    private _formBuilder: UntypedFormBuilder,
    private _changeDetectorRef: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.setSearch();
    this.setDataTable();
    this.setPage({ offset: 0 });

    this.getFilter()
    this.getOverallOverviewDetails()
  }

  ngAfterViewInit() {
    if (this._sort && this._paginator) {
      // Set the initial sort
      this._sort.sort({
        id: 'field',
        start: 'asc',
        disableClear: true,
      });

      // Mark for check
      this._changeDetectorRef.markForCheck();

      // If the user changes the sort order...
      this._sort.sortChange
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          // Reset back to the first page
          this._paginator.pageIndex = 0;

          this.onSort(this._sort)
        });


      // Get platform types if sort or page changes
      merge(this._sort.sortChange, this._paginator.page).pipe(
        switchMap(() => {
          // this.isLoading = true;

          let pageInfo = {
            'count': this._paginator.length,
            'pageSize': this._paginator.pageSize,
            'limit': this._paginator.pageSize,
            'offset': this._paginator.pageIndex
          }
          this.setPage(pageInfo); // Emit page number

          return EMPTY
        }),
        map(() => {
          this.isLoading = false;
        }),
      ).subscribe();
    }
  }

  setSearch() {
    this.searchForm = this._formBuilder.group({
      Page: [0],
      PageSize: [10],
      OrderBy: [null],
      OrderDir: [null],
      Platform: ['all'],
      Field: ['all'],
    });

    this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
    this.searchForm.get('Platform').valueChanges.subscribe(value => this.setPage({ offset: 0 }));
    this.searchForm.get('Field').valueChanges.subscribe(value => this.onSelectField());

  }

  setSize(size: number) {
    this.dt.page.size = size;
    this.dt.page.offset = 0
    this.setPage({ offset: this.dt.page.offset });
  }

  setPage(pageInfo) {
    this.dt.page.offset = pageInfo.offset;
    this.startingIndex = this.dt.page.offset * this.dt.page.size

    this.searchForm.patchValue({
      Page: this.dt.page.offset + 1,
    });

    this.wellHeaderService
      .getOverallOverview(this.searchForm.value)
      .subscribe((pagedData) => {
        this.dt.page.totalPages = pagedData.data.totalPages;
        this.dt.page.totalRecords = pagedData.data.totalRecords;
        this.dt.rows = pagedData.data.data;
      });
  }

  onSort(sort: any) {
    if (!sort) return;
    this.searchForm.patchValue({
      OrderBy: sort.active,
      OrderDir: sort.direction,
    });

    this.setPage({ offset: 0 });
  }

  setDataTable() {
    this.dt = {
      page: {
        size: this.searchForm.get('PageSize')?.value,
        totalRecords: 0,
        totalPages: 0,
        offset: 0,
      },
      rows: [],
    };
  }

  getOverallOverviewDetails() {
    this.wellHeaderService.getOverallOverview(this.searchForm.value).subscribe()
  }

  onSelectField() {
    if (this.searchForm.get('Field').value != 'all') {
      this.wellHeaderService.getWellFilter(this.searchForm.get('Field').value.fieldId).subscribe((res) => {
        this.platformList = res.data.reduce((acc, field) => acc.concat(field.platforms), []);
        this.platformList.sort((a, b) => a.platformLongName.localeCompare(b.platformLongName));
      })
    } else {
      this.getFilter()
    }
  }

  getFilter() {
    this.wellHeaderService.getWellFilter('').subscribe((res) => {
      this.fieldList = res.data.map(field => field);
      this.platformList = res.data.reduce((acc, field) => acc.concat(field.platforms), []);
      this.platformList.sort((a, b) => a.platformLongName.localeCompare(b.platformLongName));
    })
  }

  handleFormatDate(date, withTime) {
    return handleFormatDate(date, withTime)
  }

  exportToExcel() {
    if (this.searchForm.get('Platform').value == 'all') {
      this.searchForm.get('Platform')?.setValue('');
    }

    this.wellHeaderService.downloadOverallOverview('', this.searchForm.get('Platform').value).subscribe((res) => {
      saveAs(res, 'Well Production Status');
    })
  }

}
